import { NetworkStatus, WatchQueryFetchPolicy } from '@apollo/client'
import { useDebounceFn } from 'ahooks'
import { Button, Input, List, Spin } from 'antd'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { GroupAvatar } from '@/components'
import { GetGroupsFilter, useGetGroupListQuery } from '@/generated/graphql'
import { Follow } from '@/modules/follow'
import { routes } from '@/routes'

import { Pin } from '../group-pin'
import s from './groups-list.module.less'

export interface GroupsListProps {
  userId?: number
  showSearch?: boolean
  showLoadmore?: boolean
  onClickGroup: (groupId: number) => void
  needStyle?: boolean
  showFollow?: boolean
  showPin?: boolean
  type?: 'myFollowing' | 'myCreated' | 'userFollowing' | 'userCreated'
  fetchCount?: number
  showDescription?: boolean
  nameClickable?: boolean
  filter?: GetGroupsFilter
  fetchPolicy?: WatchQueryFetchPolicy
  nextFetchPolicy?: WatchQueryFetchPolicy
}

export const GroupsList: React.FC<GroupsListProps> = ({
  fetchCount = 8,
  showLoadmore = true,
  showSearch = true,
  showDescription = true,
  needStyle = true,
  showFollow = false,
  showPin = false,
  nameClickable = false,
  type = 'myFollowing',
  onClickGroup,
  userId = 0,
  filter = {},
  fetchPolicy = 'cache-and-network',
  nextFetchPolicy = 'cache-first',
}) => {
  const [t] = useTranslation()
  const [search, setSearchStr] = React.useState('')
  const { run: setSearch } = useDebounceFn(setSearchStr, { wait: 300 })

  const pagination = search ? { sortBy: 'name', first: fetchCount } : { last: fetchCount }

  const { data, error, fetchMore, networkStatus } = useGetGroupListQuery({
    fetchPolicy,
    nextFetchPolicy,
    notifyOnNetworkStatusChange: true,
    variables: {
      userId,
      ...(type === 'myFollowing' && { withViewer: true, withFollowing: true }),
      ...(type === 'myCreated' && { withViewer: true, withCreated: true }),
      ...(type === 'userFollowing' && { withUser: true, withFollowing: true }),
      ...(type === 'userCreated' && { withUser: true, withCreated: true }),
      pagination,
      filter: {
        ...(!!search && { search }),
        ...filter,
      },
    },
  })

  if (error) {
    return null
  }

  const onLoadmore = () => {
    fetchMore({
      variables: {
        pagination: {
          ...pagination,
          ...(!search && { before: pageInfo.startCursor }),
          ...(search && { after: pageInfo.endCursor }),
        },
      },
    })
  }

  const { groups, pageInfo } = (() => {
    if (!data) {
      return {
        groups: [],
        pageInfo: {
          startCursor: null,
          endCursor: null,
          hasNextPage: null,
          hasPreviousPage: null,
        },
      }
    }
    switch (type) {
      case 'myFollowing':
        return { groups: data.viewer!.followingGroups!.edges.slice(), pageInfo: data.viewer!.followingGroups!.pageInfo }
      case 'myCreated':
        return { groups: data.viewer!.createdGroups!.edges.slice(), pageInfo: data.viewer!.createdGroups!.pageInfo }
      case 'userFollowing':
        return { groups: data.user!.followingGroups!.edges.slice(), pageInfo: data.user!.followingGroups!.pageInfo }
      case 'userCreated':
        return { groups: data.user!.createdGroups!.edges.slice(), pageInfo: data.user!.createdGroups!.pageInfo }
    }
  })()

  if (!search) {
    groups.reverse()
  }

  const header = showSearch && (
    <Input.Search
      className={s.search}
      onChange={(e) => setSearch(e.currentTarget.value)}
      onSearch={(s) => setSearch(s)}
      placeholder={t('search.searchGroups')}
    />
  )

  const footer = () => {
    if (networkStatus === NetworkStatus.fetchMore) {
      return <Spin />
    }
    if (showLoadmore && (search ? pageInfo.hasNextPage : pageInfo.hasPreviousPage)) {
      return (
        <Button shape='round' onClick={onLoadmore}>
          {t('groups.grouplist.loadmore')}
        </Button>
      )
    }
    return null
  }

  return (
    <List
      className={needStyle && s.list}
      loading={networkStatus === NetworkStatus.loading}
      header={header}
      footer={footer()}
      dataSource={groups}
      split={false}
      renderItem={({ node: g }) => (
        <List.Item
          className={s.listItem}
          key={g.groupId}
          onClick={() => onClickGroup(g.groupId)}
          actions={[
            showFollow && (
              <Follow
                key='follow'
                component={<div />}
                display='icon'
                followeeGroupId={g.groupId}
                isFollowing={g.isFollowing}
              />
            ),
            showPin && <Pin groupId={g.groupId} isPinned={g.isPinned} />,
          ]}
        >
          <List.Item.Meta
            avatar={<GroupAvatar groupCatId={g.category?.groupCategoryId} />}
            title={nameClickable ? <Link to={routes.groups.page(g.groupId)}>{g.name}</Link> : g.name}
            description={showDescription && g.description}
          />
        </List.Item>
      )}
    />
  )
}
